export interface FetchJsonOptions extends Omit<RequestInit, "signal"> {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly response: Response
  ) {
    super(message);
    this.name = "HttpError";
  }
}

async function readJson(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function withTimeoutSignal(signal: AbortSignal | undefined, timeoutMs: number | undefined) {
  if (!timeoutMs) {
    return { signal, cleanup: () => undefined };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(new Error("Request timed out")), timeoutMs);
  const abortFromSource = () => controller.abort(signal?.reason);

  signal?.addEventListener("abort", abortFromSource, { once: true });

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", abortFromSource);
    },
  };
}

export async function fetchJson<T>(input: RequestInfo | URL, options: FetchJsonOptions = {}): Promise<T> {
  const { timeoutMs, signal, ...init } = options;
  const request = withTimeoutSignal(signal, timeoutMs);

  try {
    const response = await fetch(input, {
      ...init,
      signal: request.signal,
    });

    if (!response.ok) {
      const body = await readJson(response);
      const message =
        typeof body === "object" && body && "error" in body && typeof body.error === "string"
          ? body.error
          : response.statusText || `Request failed with status ${response.status}`;

      throw new HttpError(message, response.status, response);
    }

    return (await readJson(response)) as T;
  } finally {
    request.cleanup();
  }
}

export async function safeFetchJson<T>(
  input: RequestInfo | URL,
  options: FetchJsonOptions = {}
): Promise<T | null> {
  try {
    return await fetchJson<T>(input, options);
  } catch {
    return null;
  }
}
